/**
 * Telling a user's grid action from the grid's own bookkeeping.
 *
 * In its own module for the same reason as `data-frames.ts`: a module exporting
 * both a component and a plain function cannot be hot-reloaded, and the failure
 * is a stale module rather than a refused edit.
 */

/**
 * Grid-originated event sources — everything the grid does to itself rather than
 * because the user asked.
 *
 * Persisting these is what creates a write -> rebuild -> write loop, and they
 * fire constantly: `gridInitializing` on mount, `flex`/`sizeColumnsToFit`
 * whenever the container is resized (so every frame of a layout-divider drag),
 * and `api` for state we ourselves just applied.
 *
 * A denylist rather than an allowlist so an unrecognised *user* action still
 * persists; the value-keyed `columnDefs` memo is the backstop that makes a
 * missed entry harmless rather than a hang.
 */
const GRID_ORIGINATED = new Set([
  "api",
  "gridInitializing",
  "gridOptionsChanged",
  "flex",
  "sizeColumnsToFit",
  "autosizeColumns",
  "autosizeColumnHeaders",
  "alignedGridChanged",
  "alignedGridScrolled",
  "rowModelUpdated",
  "columnRowGroupChanged",
  "columnPivotChanged",
  "filterDestroyed",
]);

export function isUserOriginated(source: string | undefined): boolean {
  return source === undefined ? false : !GRID_ORIGINATED.has(source);
}
